import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Spinner,
  Badge,
  Textarea,
  Divider,
} from '@chakra-ui/react';
import { SurveyForm as SurveyFormAPI } from '@devd-client/api';
import type {
  PriorityAreasData,
  SelectedPriorityArea,
  PriorityArea,
} from './types';

const { surveyApi } = SurveyFormAPI;

interface PriorityAreasFormProps {
  surveyId: string;
  token: string;
  onComplete: () => void;
}

const PriorityAreasForm: React.FC<PriorityAreasFormProps> = ({
  surveyId,
  token,
  onComplete,
}) => {
  const [priorityData, setPriorityData] = useState<PriorityAreasData | null>(
    null
  );
  const [selectedAreas, setSelectedAreas] = useState<SelectedPriorityArea[]>(
    []
  );
  const [comments, setComments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadPriorityAreas();
  }, []);

  const loadPriorityAreas = async () => {
    try {
      const data: PriorityAreasData = await surveyApi.fetchPriorityAreas(
        surveyId,
        token
      );
      setPriorityData(data);
    } catch (err: any) {
      // Check if it's a 404 error - if so, redirect to thank you page
      if (err.status === 404) {
        onComplete();
        return;
      }
      setError('Failed to load priority areas. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleAreaSelection = (
    area: PriorityArea,
    categoryKey: string,
    isSelected: boolean
  ) => {
    if (isSelected) {
      if (priorityData && selectedAreas.length >= priorityData.maxSelections) {
        alert(`You can only select up to ${priorityData.maxSelections} areas`);
        return;
      }

      const newArea: SelectedPriorityArea = {
        areaKey: area.areaKey,
        areaText: area.areaText,
        ratingCategory: categoryKey,
        priorityRank: selectedAreas.length + 1,
        comment: null,
      };

      setSelectedAreas([...selectedAreas, newArea]);
    } else {
      const filtered = selectedAreas
        .filter((sa) => sa.areaKey !== area.areaKey)
        .map((sa, index) => ({ ...sa, priorityRank: index + 1 }));

      setSelectedAreas(filtered);

      const newComments = { ...comments };
      delete newComments[area.areaKey];
      setComments(newComments);
    }
  };

  const handleCommentChange = (areaKey: string, comment: string) => {
    setComments((prev) => ({
      ...prev,
      [areaKey]: comment,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (selectedAreas.length < 2) {
      alert('Please select at least 2 areas');
      return;
    }

    setSubmitting(true);

    try {
      const areasWithComments = selectedAreas.map((area) => ({
        ...area,
        comment: comments[area.areaKey] || null,
      }));

      await surveyApi.submitPriorityAreas(surveyId, token, areasWithComments);
      onComplete();
    } catch (err: any) {
      alert('Failed to submit priority areas. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container maxW="container.md" py={20}>
        <VStack spacing={4}>
          <Spinner size="xl" color="primary" />
          <Text>Loading priority areas...</Text>
        </VStack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxW="container.md" py={20}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      </Container>
    );
  }

  if (!priorityData) {
    return (
      <Container maxW="container.md" py={20}>
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          Failed to load priority areas
        </Alert>
      </Container>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="container.md" py={10}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Text fontSize="3xl" fontWeight="bold" color="text.primary">
              {priorityData.title}
            </Text>
            <Text fontSize="md" color="text.secondary" maxW="600px">
              {priorityData.description}
            </Text>
            <HStack spacing={4} flexWrap="wrap" justify="center">
              <Badge
                colorScheme={priorityData.isAnonymous ? 'blue' : 'red'}
                variant="subtle"
              >
                {priorityData.isAnonymous
                  ? 'Anonymous responses'
                  : 'Responses in this step are not anonymous'}
              </Badge>
              <Text fontSize="sm" color="text.secondary">
                Select {selectedAreas.length}/2-{priorityData.maxSelections}{' '}
                areas
              </Text>
            </HStack>
          </VStack>

          {/* Priority Areas Form */}
          <Box
            bg="white"
            p={8}
            borderRadius="lg"
            shadow="sm"
            border="1px"
            borderColor="gray.200"
          >
            <form onSubmit={handleSubmit}>
              <VStack spacing={8} align="stretch">
                {priorityData.ratingCategories.map((category) => {
                  if (!category.areas || category.areas.length === 0) {
                    return null;
                  }

                  return (
                    <Box key={category.categoryKey}>
                      <Text
                        fontSize="xl"
                        fontWeight="semibold"
                        color="text.primary"
                        mb={4}
                      >
                        {category.categoryName}
                      </Text>

                      <VStack spacing={4} align="stretch">
                        {category.areas.map((area) => {
                          const isSelected = selectedAreas.some(
                            (sa) => sa.areaKey === area.areaKey
                          );
                          const selectedArea = selectedAreas.find(
                            (sa) => sa.areaKey === area.areaKey
                          );

                          return (
                            <Box
                              key={area.areaKey}
                              p={4}
                              border="1px"
                              borderColor={isSelected ? 'blue.200' : 'gray.200'}
                              borderRadius="md"
                              bg={isSelected ? 'blue.50' : 'white'}
                            >
                              <FormControl>
                                <HStack align="flex-start" spacing={3}>
                                  <Checkbox
                                    isChecked={isSelected}
                                    onChange={(e) =>
                                      handleAreaSelection(
                                        area,
                                        category.categoryKey,
                                        e.target.checked
                                      )
                                    }
                                    colorScheme="blue"
                                    size="lg"
                                    mt={1}
                                  />
                                  <VStack align="stretch" flex={1} spacing={2}>
                                    <HStack justify="space-between">
                                      <Text
                                        fontSize="md"
                                        fontWeight="medium"
                                        color="text.primary"
                                      >
                                        {area.areaText}
                                      </Text>
                                      {isSelected && selectedArea && (
                                        <Badge
                                          colorScheme="blue"
                                          variant="solid"
                                          fontSize="xs"
                                        >
                                          #{selectedArea.priorityRank}
                                        </Badge>
                                      )}
                                    </HStack>

                                    {isSelected && area.allowComments && (
                                      <Box mt={3}>
                                        <FormLabel
                                          fontSize="sm"
                                          color="text.secondary"
                                          mb={2}
                                        >
                                          Add an optional comment:
                                        </FormLabel>
                                        <Textarea
                                          placeholder="Share more details about why this area needs improvement..."
                                          value={comments[area.areaKey] || ''}
                                          onChange={(e) =>
                                            handleCommentChange(
                                              area.areaKey,
                                              e.target.value
                                            )
                                          }
                                          size="sm"
                                          resize="vertical"
                                          minH="80px"
                                        />
                                      </Box>
                                    )}
                                  </VStack>
                                </HStack>
                              </FormControl>
                            </Box>
                          );
                        })}
                      </VStack>
                      <Divider mt={6} />
                    </Box>
                  );
                })}

                <Box pt={4}>
                  <Button
                    type="submit"
                    colorScheme="brand"
                    size="lg"
                    width="full"
                    isLoading={submitting}
                    loadingText="Submitting..."
                    isDisabled={selectedAreas.length < 2}
                  >
                    Submit Priority Areas
                  </Button>
                  {selectedAreas.length < 2 && (
                    <Text
                      fontSize="sm"
                      color="red.500"
                      textAlign="center"
                      mt={2}
                    >
                      Please select at least 2 areas to continue
                    </Text>
                  )}
                </Box>
              </VStack>
            </form>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default PriorityAreasForm;
